import type { ScheduleTaskType } from '@risksmart-app/domain/src/types/consts';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledGroupAndUserMultiSelect from 'src/components/form/controlled-group-and-user-multi-select';
import ControlledInput from 'src/components/form/controlled-input';
import type { StatusType } from 'src/components/form/controlled-select/ControlledSelect';
import ControlledTextarea from 'src/components/form/controlled-textarea';

import { useGetSchedulableTargets } from '@/hooks/queries/scheduler/useGetSchedulableTargets';
import { useGetScheduleTaskTypes } from '@/hooks/queries/scheduler/useGetScheduleTaskTypes';

import { AssigneeField } from './AssigneeField';
import type { ScheduleFormFieldData } from './scheduleFormSchema';
import {
  orderTargetsByTaskType,
  toAssigneeOptions,
  toTargetOptions,
} from './scheduleTargets';
import { TargetField } from './TargetField';
import type { TaskTypeOption } from './TaskTypeField';
import { TaskTypeField } from './TaskTypeField';

const EMPTY_POOL = { users: [], groups: [] };

export const ScheduleDetailsFields: FC = () => {
  const { t } = useTranslation(['common']);
  const { control } = useFormContext<ScheduleFormFieldData>();
  const taskTypes = useWatch({ control, name: 'TaskTypes' });
  const targets = useWatch({ control, name: 'Targets' });

  const {
    data: taskTypeRows,
    isPending: taskTypesPending,
    isError: taskTypesFailed,
  } = useGetScheduleTaskTypes();
  const {
    data: schedulableTargets,
    isPending: targetsPending,
    isError: targetsFailed,
  } = useGetSchedulableTargets(taskTypes);

  // A failed read leaves the options empty, which a 'finished' picker renders as
  // "no matches" — indistinguishable from an org that owns no controls.
  const toStatusType = (pending: boolean, failed: boolean): StatusType => {
    if (pending) {
      return 'loading';
    }

    return failed ? 'error' : 'finished';
  };

  const taskTypeOptions = useMemo<TaskTypeOption[]>(
    () =>
      (taskTypeRows ?? []).map((row) => ({
        value: row.Value,
        label: row.Label,
        // Client-side and keyed on the type's value, so a newly seeded type
        // renders label-only until someone adds the string.
        description:
          t(`scheduler.task_type_descriptions.${row.Value}`, {
            defaultValue: '',
          }) || undefined,
      })),
    [taskTypeRows, t]
  );

  const selectedTaskTypes = useMemo(
    () => (taskTypeRows ?? []).filter((row) => taskTypes.includes(row.Value)),
    [taskTypeRows, taskTypes]
  );

  const assigneeOptionsByTarget = useMemo(
    () =>
      new Map(
        (schedulableTargets ?? []).map((target) => [
          target.TargetId,
          toAssigneeOptions(target),
        ])
      ),
    [schedulableTargets]
  );

  const orderedTargets = useMemo(
    () =>
      orderTargetsByTaskType(
        targets,
        selectedTaskTypes.map((row) => row.Value)
      ),
    [selectedTaskTypes, targets]
  );

  const targetOptionsByTaskType = useMemo(
    () =>
      new Map<ScheduleTaskType, ReturnType<typeof toTargetOptions>>(
        selectedTaskTypes.map((row) => [
          row.Value,
          toTargetOptions(schedulableTargets ?? [], row.Value, row.TargetType),
        ])
      ),
    [selectedTaskTypes, schedulableTargets]
  );

  return (
    <>
      <ControlledInput
        control={control}
        name={'Name'}
        label={t('scheduler.fields.title')}
        testId={'schedule-name'}
      />

      <ControlledTextarea
        control={control}
        name={'Description'}
        label={t('scheduler.fields.description')}
        testId={'schedule-description'}
      />

      {/*
        `groupFilter` is deliberately left at its default of
        `(group) => group.OwnerContributor`: schedule owners become real
        `owner_group` rows on the schedule node, which is exactly what that flag
        marks a group as eligible for.
      */}
      <ControlledGroupAndUserMultiSelect
        control={control}
        name={'Owners'}
        label={t('scheduler.fields.owners')}
        includeGroups={true}
        testId={'schedule-owners'}
      />

      {/*
        The three fields below appear and multiply as the planner works, so they
        come last: task type produces the target pickers, and each target
        produces an assignee picker. Interleaving them with the always-present
        fields above pushed filled-in fields down the page mid-edit and split one
        cascade across the section.
      */}
      <TaskTypeField
        options={taskTypeOptions}
        statusType={toStatusType(taskTypesPending, taskTypesFailed)}
      />

      {selectedTaskTypes.map((row) => (
        <TargetField
          key={row.Value}
          taskType={row.Value}
          label={t('scheduler.fields.targets', { taskType: row.Label })}
          options={targetOptionsByTaskType.get(row.Value) ?? []}
          statusType={toStatusType(targetsPending, targetsFailed)}
        />
      ))}

      {orderedTargets.map((target) => (
        <AssigneeField
          key={target.value}
          target={target}
          statusType={toStatusType(targetsPending, targetsFailed)}
          {...(assigneeOptionsByTarget.get(target.value) ?? EMPTY_POOL)}
        />
      ))}
    </>
  );
};
